import type { Config } from 'tailwindcss';

/** @type {import('tailwindcss').Config} */
export default {
  content: ['./index.html', './src/**/*.{js,ts,jsx,tsx}'],
  safelist: [
    'bg-primary',
    'bg-orange',
    'bg-primary/20',
    'bg-primary/40',
    'bg-primary/60',
    'bg-primary/80',
    'bg-primary/100',
    'hover:bg-opacity-[0.16]',
    '[&::-webkit-slider-thumb]:h-[6px]',
    '[&::-webkit-slider-thumb]:h-[9px]',
    '[&::-webkit-slider-thumb]:h-[12px]',
    '[&::-webkit-slider-thumb]:w-[6px]',
    '[&::-webkit-slider-thumb]:w-[9px]',
    '[&::-webkit-slider-thumb]:w-[12px]',
  ],
  theme: {
    extend: {
      fontFamily: {
        sans: ['DM Sans'],
      },
      fontSize: {
        xxs: ['0.625rem', '0.875rem'],
        xs: ['.75rem', '1rem'],
        sm: ['.875rem', '1.3125rem'],
        '3.5xl': ['2rem', '2.1875rem'],
      },
      colors: {
        primary: {
          DEFAULT: '#2540F5',
          100: '#EBEEFE',
          200: '#D3D9FD',
          300: '#E8EBFD',
          400: '#8796F9',
          500: '#2540F5',
          600: '#0900C7',
          700: '#000081',
          800: '#061052',
          900: '#02072D',
          disabledBg: '#E8ECFE',
          disabledText: '#A8AFE5',
        },
        secondary: {
          DEFAULT: '#00FF8B',
          500: '#00FF8B',
          600: '#00C76C',
          700: '#008F4E',
        },
        green: {
          DEFAULT: '#10B981',
          100: '#ECFDF5',
          500: '#10B981',
          600: '#059669',
          700: '#047857',
          800: '#065F46',
        },
        orange: {
          DEFAULT: '#DB7805',
          300: '#E8AA00',
          500: '#DB7805',
        },
        red: {
          DEFAULT: '#D13637',
          500: '#D13637',
          100: '#FAEDED',
          disabledBg: '#C9C8C8',
          disabledText: '#979797',
        },
        gray: {
          DEFAULT: '#787878',
          100: '#D9D9D9',
          200: '#DCE2E8',
          300: '#E5E5E5',
          400: '#B9B9B9',
          500: '#787878',
          600: '#7D7D7D',
          700: '#636363',
        },
        lightBlue: '#F8FAFB',
        black: {
          DEFAULT: '#222222',
          500: '#222222',
        },
        aiInterviewBanner: {
          background: '#242943',
          icon: '#4153C4',
          lightText: '#D4D2D2',
        },
      },
      height: {
        'full-device': '100dvh',
      },
      minHeight: {
        'full-device': '100dvh',
      },
      maxHeight: {
        'full-device': '100dvh',
      },
      boxShadow: {
        'sm-primary': '0px 4px 40px 0px #1C36E21A',
        md: '0px 4px 20px 0px rgba(3, 20, 82, 0.08)',
        whiteTopBar: '0 0 40px rgba(28, 54, 226, 0.1)',
        'md-primary': '0px 4px 40px 0px rgba(28, 54, 226, 0.1)',
        'step-footer': [
          '-71px -39px 23px 0px rgba(168, 168, 168, 0)',
          '-45px -25px 21px 0px rgba(168, 168, 168, 0.01)',
          '-25px -14px 17px 0px rgba(168, 168, 168, 0.05)',
          '-11px -6px 13px 0px rgba(168, 168, 168, 0.09)',
          '-3px -2px 7px 0px rgba(168, 168, 168, 0.10)',
        ].join(', '),
        'md-white': '-4px -4px 20px 0px #0314521F',
        faq: [
          '-5px 4px 15px 0px rgba(138, 138, 138, 0.10)',
          '-21px 16px 27px 0px rgba(138, 138, 138, 0.09)',
          '-48px 36px 36px 0px rgba(138, 138, 138, 0.05)',
          '-86px 65px 43px 0px rgba(138, 138, 138, 0.01)',
          '-134px 101px 47px 0px rgba(138, 138, 138, 0.00)',
        ].join(', '),
        drawer: `
          -134px 101px 47px 0px rgba(138,138,138,0.00),
          -21px 16px 27px 0px rgba(138,138,138,0.09),
          -48px 36px 36px 0px rgba(138,138,138,0.05),
          -5px 4px 15px 0px rgba(138,138,138,0.10),
          -86px 65px 43px 0px rgba(138,138,138,0.01)
        `,
      },
      spacing: {
        '4.5': '18px',
        '5.5': '22px',
        '6.5': '26px',
        '8.5': '34px',
        '11.25': '45px',
        '12.5': '50px',
        '13.5': '55px',
        14: '56px',
        15: '60px',
        17: '68px',
        18: '72px',
        19: '76px',
        20.5: '82px',
        21: '84px',
        25: '100px',
        29: '116px',
        30: '120px',
        37.5: '150px',
        50: '200px',
        100: '400px',
        '102.5': '410px',
        '110.5': '442px',
        112.5: '450px',
        124: '496px',
        '129': '516px',
        '132.5': '530px',
        '140.5': '562px',
        193: '763px',
      },
      maxWidth: {
        container: '1920px',
        'container-onb': '688px',
        'container-profile': '1168px',
        input: '320px',
        'input-profile': '350px',
        screen: '100dvw',
        sm: '560px',
        md: '684px',
        lg: '806px',
      },
      backgroundImage: {
        'ellipse-middle': "url('/src/assets/ellipse_middle.svg')",
        'ellipse-bottom': "url('/src/assets/ellipse_bottom.svg')",
        'ellipse-tl': "url('/src/assets/ellipse_tl.svg')",
        'mobile-ellipse-tl': "url('/src/assets/mobile-ellipse-tl.svg')",
        'mobile-ellipse-middle': "url('/src/assets/mobile-ellipse-middle.svg')",
        'custom-gradient': 'linear-gradient(160.096deg, #2540F5 0%, #0E1C7B 50%, #061052 77%)',
        'custom-gradient-mobile': 'linear-gradient(-178.321deg, #2540F5 0%, #061052 98%);',
        'blue-top-bar-gradient': 'linear-gradient(169deg, #2540F5 21.81%, #061052 123.03%)',
        'button-gradient': 'linear-gradient(96.57deg, #2540F5 3.88%, #16258F 98.14%)',
        'otp-gradient': ' linear-gradient(166.719deg, #2540F5 0%, #061052 100%);',
        'primary-gradient-horizontal':
          'linear-gradient(168.71deg, #2540F5 21.81%, #061052 123.03%);',
        'air-gradient': 'linear-gradient(169deg, #2540F5 21.81%, #061052 123.03%);',
        'air-prepping': "url('/src/assets/bg-big.svg')",
        'air-prepping-mobile-gradient':
          'linear-gradient(180deg, rgba(37,64,245,0.3) 0%, rgba(37,64,245,0.6) 70%, rgba(255,255,255,0) 93%)',
      },
      content: {
        'topbar-ellipse-left': 'url("/src/assets/topbar/ellipse-left.svg")',
        'topbar-ellipse-right': 'url("/src/assets/topbar/ellipse-right.svg")',
      },
      opacity: {
        3: '0.03',
      },
      transitionProperty: {
        shadow: 'box-shadow',
      },
      keyframes: {
        'fade-in-right': {
          '0%': { opacity: '0', transform: 'translateX(24px)' },
          '100%': { opacity: '1', transform: 'translateX(0)' },
        },
        'fade-in-left': {
          '0%': { opacity: '0', transform: 'translateX(-24px)' },
          '100%': { opacity: '1', transform: 'translateX(0)' },
        },
        'fade-in-up': {
          '0%': { opacity: '0', transform: 'translateY(12px)' },
          '100%': { opacity: '1', transform: 'translateY(0)' },
        },
        'fade-in-up-sm': {
          '0%': { opacity: '0', transform: 'translateY(4px)' },
          '100%': { opacity: '1', transform: 'translateY(0)' },
        },
      },
      animation: {
        'fade-in-right': 'fade-in-right 200ms cubic-bezier(0.22, 1, 0.36, 1) both',
        'fade-in-left': 'fade-in-left 200ms cubic-bezier(0.22, 1, 0.36, 1) both',
        'fade-in-up': 'fade-in-up 300ms cubic-bezier(0.22, 1, 0.36, 1) both',
        'fade-in-up-sm': 'fade-in-up-sm 150ms cubic-bezier(0.22, 1, 0.36, 1) both',
      },
    },
  },
  plugins: [],
} satisfies Config;
